/** Tag schema → General tab. Name, codename, description. */
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Stack, TextField } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import * as tagSchemasApi from '@/api/tagSchemas';
import type { TagSchemaDetail } from '@/api/tagSchemas';
import { EditorSaveBar } from '@/components/EditorSaveBar/EditorSaveBar';
import { useSnackbar } from '@/components/Snackbar';
import { usePermissions } from '@/hooks/usePermissions';

const Schema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(200),
  codename: z
    .string()
    .trim()
    .min(1, 'Codename is required')
    .max(64)
    .regex(/^[-a-z0-9_]+$/, 'Lowercase letters, digits, - and _ only'),
  description: z.string().max(4000),
});
type FormValues = z.infer<typeof Schema>;

const toDefaults = (s: TagSchemaDetail): FormValues => ({
  name: s.name,
  codename: s.codename,
  description: s.description ?? '',
});

export function TagSchemaGeneralTab({ schema, onSaved }: { schema: TagSchemaDetail; onSaved: () => void }) {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_tag_schemas_manage');
  const snackbar = useSnackbar();
  const { control, handleSubmit, reset, formState, setError } = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: toDefaults(schema),
  });

  useEffect(() => {
    reset(toDefaults(schema));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema]);

  const patch = useMutation({
    mutationFn: (body: tagSchemasApi.TagSchemaWrite) => tagSchemasApi.update(schema.id, body),
    onSuccess: () => {
      snackbar.show('Saved.', 'success');
      onSaved();
    },
    onError: (e) => {
      if (e instanceof ConsoleApiError && e.status === 400) {
        const body = e.body as Record<string, unknown> | undefined;
        if (body && typeof body === 'object' && 'codename' in body) {
          setError('codename', { message: 'That codename is already taken.' });
          return;
        }
      }
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error');
    },
  });

  const submit = handleSubmit((v) =>
    patch.mutate({ name: v.name.trim(), codename: v.codename.trim(), description: v.description }),
  );
  const err = formState.errors;

  return (
    <form onSubmit={submit}>
      <Stack spacing={2} sx={{ maxWidth: 640 }}>
        <Controller control={control} name="name" render={({ field }) => (
          <TextField {...field} size="small" fullWidth disabled={!canManage} label="Name"
            error={!!err.name} helperText={err.name?.message} />
        )} />
        <Controller control={control} name="codename" render={({ field }) => (
          <TextField {...field} size="small" fullWidth disabled={!canManage} label="Codename"
            error={!!err.codename} helperText={err.codename?.message ?? 'Unique slug used to reference this schema.'} />
        )} />
        <Controller control={control} name="description" render={({ field }) => (
          <TextField {...field} size="small" fullWidth multiline minRows={2} disabled={!canManage}
            label="Description" error={!!err.description} helperText={err.description?.message} />
        )} />
      </Stack>

      <Box sx={{ mt: 2 }}>
        <EditorSaveBar
          dirty={formState.isDirty} saving={patch.isPending} canManage={canManage}
          onSave={submit} onDiscard={() => reset(toDefaults(schema))}
        />
      </Box>
    </form>
  );
}
